// src/pages/offlineSales/components/OfflineSalesItemsSection.jsx
// Rebuilt to match the online order form's OrderItemsSection.jsx UX (Stage 7/8 follow-up) —
// reuses useProductSearch and DiscountPercentagePopup verbatim, without touching
// OrderItemsSection.jsx itself (no cross-sell concept here, so that part is omitted).
import React, { useState, useRef } from 'react';
import { Search, Plus, X, Package, Hash, DollarSign, Minus, AlertCircle, Percent, Gift } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import useProductSearch from '../../../hooks/useProductSearch';
import { calculateLineTotal, getEffectiveLineDiscount } from '../../../utils/orderCalculations';
import DiscountPercentagePopup from '../../../components/ui/DiscountPercentagePopup';
import styles from '../../orders/components/OrderItemsSection.module.css';
import FreeUnitsPopup from './FreeUnitsPopup';
import freeStyles from './OfflineSalesItemsSection.module.css';

const OfflineSalesItemsSection = ({
  shopId,
  items,
  errors,
  canChangeUnitPrice,
  canGiveDiscount,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
}) => {
  const { t } = useLocalization();
  const [localInputValues, setLocalInputValues] = useState({});
  const [discountPopupState, setDiscountPopupState] = useState(null);
  const [freePopupState, setFreePopupState] = useState(null);
  const searchInputRef = useRef(null);

  const {
    searchTerm,
    searchResults,
    isSearching: isLoadingResults,
    selectedIndex,
    showResults,
    hasSearched,
    resultsRef,
    handleSearchChange,
    handleKeyDown: productSearchKeyDown,
    selectProduct,
    clearSearch,
    hideResults,
    showResultsDropdown,
  } = useProductSearch(shopId);

  const handleProductSelect = (product) => {
    const selectedProduct = selectProduct(product);
    onAddItem(selectedProduct);
    clearSearch();

    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (searchResults.length > 0) {
        const selectedProduct = selectedIndex >= 0 ? searchResults[selectedIndex] : searchResults[0];
        handleProductSelect(selectedProduct);
        return;
      }
    }
    const selectedProduct = productSearchKeyDown(e);
    if (selectedProduct) {
      handleProductSelect(selectedProduct);
    }
  };

  const formatNumberForDisplay = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    return num.toString();
  };

  const getPriceDisplayValue = (item, field) => {
    const key = `${item.key}-${field}`;
    if (key in localInputValues) return localInputValues[key];
    const raw = field === 'unitPrice' ? item.unitPrice : (item.lineDiscount || 0);
    return formatNumberForDisplay(raw);
  };

  const handlePriceChange = (e, field, itemKey) => {
    const raw = e.target.value;
    const key = `${itemKey}-${field}`;
    if (raw === '') {
      setLocalInputValues(prev => ({ ...prev, [key]: raw }));
      onUpdateItem(itemKey, field, 0);
      return;
    }
    if (!/^\d*\.?\d{0,2}$/.test(raw)) return;
    setLocalInputValues(prev => ({ ...prev, [key]: raw }));
    const numValue = parseFloat(raw);
    if (!isNaN(numValue)) {
      onUpdateItem(itemKey, field, numValue);
    }
  };

  const handlePriceBlur = (itemKey, field) => {
    const key = `${itemKey}-${field}`;
    setLocalInputValues(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const renderSearchResults = () => {
    if (!showResults || !shopId) return null;
    return (
      <div className={styles.searchResults} ref={resultsRef}>
        {isLoadingResults ? (
          <div className={styles.searchLoading}>
            <div className={styles.loadingSpinner}></div>
            <span>{t('common.loading')}</span>
          </div>
        ) : searchResults.length > 0 ? (
          <div className={styles.resultsList}>
            {searchResults.map((product, index) => (
              <div
                key={`${product.productId}-${product.variationId || 'simple'}`}
                className={`${styles.resultItem} ${index === selectedIndex ? styles.selected : ''} ${product.isOutOfStock ? styles.outOfStock : ''}`}
                onClick={() => handleProductSelect(product)}
              >
                <div className={styles.productInfo}>
                  <div className={styles.productName}>{product.formattedName}</div>
                  <div className={styles.productDetails}>
                    <span className={styles.productSku}>
                      <Hash size={12} />
                      {product.sku || 'No SKU'}
                    </span>
                    {!product.stockHidden && (
                      <span className={styles.productStock}>
                        <Package size={12} />
                        {product.formattedStock}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.productPrice}>{product.formattedPrice}</div>
              </div>
            ))}
          </div>
        ) : hasSearched ? (
          <div className={styles.noResults}>
            <AlertCircle size={16} />
            <span>{t('offlineSales.noProductsFound')}</span>
          </div>
        ) : null}
      </div>
    );
  };

  const renderItem = (item, index) => {
    const freeQuantity = item.freeQuantity || 0;
    const freeValue = freeQuantity * (parseFloat(item.unitPrice) || 0);
    const effectiveDiscount = getEffectiveLineDiscount(item);
    const lineCalculation = calculateLineTotal(item.quantity, item.unitPrice, effectiveDiscount);
    const freeDisabled = !canGiveDiscount || item.quantity <= 1;
    const freePopupOpen = freePopupState?.itemKey === item.key;

    return (
      <div key={item.key} className={styles.orderItem}>
        <div className={styles.itemHeader}>
          <div className={styles.itemInfo}>
            <h4 className={styles.itemName}>{item.name}</h4>
          </div>
          <button
            type="button"
            onClick={() => {
              if (freePopupOpen) setFreePopupState(null);
              onRemoveItem(item.key);
            }}
            className={styles.removeItemButton}
            title={t('offlineSales.removeItem')}
          >
            <X size={16} />
          </button>
        </div>

        <div className={styles.itemFields}>
          <div className={styles.fieldGroup}>
            <label>{t('offlineSales.quantity')} *</label>
            <div className={styles.discountFieldRow}>
            <div className={styles.quantityWrapper}>
              <button
                type="button"
                onClick={() => onUpdateItem(item.key, 'quantity', Math.max(1, item.quantity - 1))}
                className={styles.quantityButton}
                disabled={item.quantity <= 1}
              >
                <Minus size={14} />
              </button>
              <input
                type="number"
                value={formatNumberForDisplay(item.quantity)}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    onUpdateItem(item.key, 'quantity', 1);
                  } else {
                    const intValue = parseInt(value);
                    if (!isNaN(intValue) && intValue >= 1) onUpdateItem(item.key, 'quantity', intValue);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === '.' || e.key === ',' || e.key === '-' || e.key === 'e') e.preventDefault();
                }}
                min="1"
                step="1"
                className={`${styles.quantityInput} ${errors.itemDetails?.[index]?.quantity ? styles.error : ''}`}
              />
              <button
                type="button"
                onClick={() => onUpdateItem(item.key, 'quantity', parseFloat(item.quantity) + 1)}
                className={styles.quantityButton}
              >
                <Plus size={14} />
              </button>
            </div>
            <button
              type="button"
              className={`${styles.discountPctBtn} ${freeQuantity > 0 || freePopupOpen ? styles.discountPctBtnActive : ''} ${freeStyles.freeGiftBtn}`}
              onClick={(e) => setFreePopupState(freePopupOpen ? null : { itemKey: item.key, anchorEl: e.currentTarget })}
              disabled={freeDisabled}
              title={t('offlineSales.showFreeUnits')}
            >
              <Gift size={12} />
              {freeQuantity > 0 && <span className={freeStyles.freeBadge}>{freeQuantity}</span>}
            </button>
            {freePopupOpen && (
              <FreeUnitsPopup
                anchorEl={freePopupState.anchorEl}
                quantity={item.quantity}
                currentValue={freeQuantity}
                onApply={(value) => onUpdateItem(item.key, 'freeQuantity', value)}
                onClose={() => setFreePopupState(null)}
              />
            )}
            </div>
            {errors.itemDetails?.[index]?.quantity && (
              <span className={styles.errorText}>{errors.itemDetails[index].quantity}</span>
            )}
            {errors.itemDetails?.[index]?.freeQuantity && (
              <span className={styles.errorText}>{errors.itemDetails[index].freeQuantity}</span>
            )}
          </div>

          <div className={styles.fieldGroup}>
            <label>
              {t('offlineSales.unitPrice')} *
              {!canChangeUnitPrice && <span className={styles.lockedField}>🔒</span>}
            </label>
            <div className={styles.inputWrapper}>
              <DollarSign className={styles.inputIcon} size={16} />
              <input
                type="text"
                value={getPriceDisplayValue(item, 'unitPrice')}
                onChange={(e) => handlePriceChange(e, 'unitPrice', item.key)}
                onBlur={() => handlePriceBlur(item.key, 'unitPrice')}
                disabled={!canChangeUnitPrice}
                className={`${styles.priceInput} ${errors.itemDetails?.[index]?.unitPrice ? styles.error : ''}`}
              />
            </div>
            {errors.itemDetails?.[index]?.unitPrice && (
              <span className={styles.errorText}>{errors.itemDetails[index].unitPrice}</span>
            )}
          </div>

          <div className={styles.fieldGroup}>
            <label>
              {t('offlineSales.lineDiscount')}
              {!canGiveDiscount && <span className={styles.lockedField}>🔒</span>}
            </label>
            <div className={styles.discountFieldRow}>
              <div className={styles.inputWrapper}>
                <DollarSign className={styles.inputIcon} size={16} />
                <input
                  type="text"
                  value={getPriceDisplayValue(item, 'lineDiscount')}
                  onChange={(e) => handlePriceChange(e, 'lineDiscount', item.key)}
                  onBlur={() => handlePriceBlur(item.key, 'lineDiscount')}
                  disabled={!canGiveDiscount}
                  placeholder="0"
                  className={`${styles.priceInput} ${errors.itemDetails?.[index]?.lineDiscount ? styles.error : ''}`}
                />
              </div>
              {canGiveDiscount && (
                <button
                  type="button"
                  className={`${styles.discountPctBtn} ${discountPopupState?.itemKey === item.key ? styles.discountPctBtnActive : ''}`}
                  onClick={(e) => setDiscountPopupState(
                    discountPopupState?.itemKey === item.key ? null : { itemKey: item.key, anchorEl: e.currentTarget }
                  )}
                  title="Set discount as percentage"
                >
                  <Percent size={12} />
                </button>
              )}
              {discountPopupState?.itemKey === item.key && (
                <DiscountPercentagePopup
                  anchorEl={discountPopupState.anchorEl}
                  unitPrice={item.unitPrice}
                  quantity={item.quantity - freeQuantity}
                  onApply={(amount) => {
                    setLocalInputValues(prev => {
                      const next = { ...prev };
                      delete next[`${item.key}-lineDiscount`];
                      return next;
                    });
                    onUpdateItem(item.key, 'lineDiscount', amount);
                  }}
                  onClose={() => setDiscountPopupState(null)}
                />
              )}
            </div>
            {errors.itemDetails?.[index]?.lineDiscount ? (
              <span className={styles.errorText}>{errors.itemDetails[index].lineDiscount}</span>
            ) : freeQuantity > 0 && (
              <span className={freeStyles.discountCaption}>
                <span className={freeStyles.discountCaptionTotal}>
                  {t('offlineSales.totalDiscountLabel', { total: effectiveDiscount.toFixed(2) })}
                </span>
                {' · '}
                {t('offlineSales.inclFreeValue', { value: freeValue.toFixed(2), count: freeQuantity })}
              </span>
            )}
          </div>

          <div className={styles.fieldGroup}>
            <label>{t('offlineSales.lineTotal')}</label>
            <div className={styles.lineTotalDisplay}>Rs. {lineCalculation.lineTotal.toFixed(2)}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.orderItemsContent}>
      <div className={styles.productSearch}>
        <div className={styles.searchSection}>
          <div className={styles.searchHeader}>
            <h3>{t('offlineSales.searchProducts')}</h3>
          </div>
          <div className={styles.searchWrapper}>
            <div className={styles.searchInputWrapper}>
              <Search className={styles.searchIcon} size={18} />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                onFocus={showResultsDropdown}
                onBlur={() => setTimeout(hideResults, 200)}
                placeholder={t('offlineSales.searchProducts')}
                className={styles.searchInput}
                autoComplete="off"
                disabled={!shopId}
              />
              {searchTerm && (
                <button type="button" onClick={clearSearch} className={styles.clearSearchButton}>
                  <X size={16} />
                </button>
              )}
            </div>
            {renderSearchResults()}
          </div>
        </div>
      </div>

      <div className={styles.orderItemsList}>
        {items.length > 0 ? (
          <div className={styles.itemsContainer}>
            <div className={styles.itemsList}>
              {items.map((item, index) => renderItem(item, index))}
            </div>
          </div>
        ) : (
          <div className={styles.emptyState}>
            <Package size={48} />
            <h3>{t('offlineSales.noItemsAdded')}</h3>
          </div>
        )}
        {errors.items && (
          <div className={styles.sectionError}>
            <AlertCircle size={16} />
            {errors.items}
          </div>
        )}
      </div>
    </div>
  );
};

export default OfflineSalesItemsSection;
